import { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,

  Box,
  CircularProgress,
  Divider,
  makeStyles,
  Chip,
} from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  InfoCard,
  EmptyState,
  MarkdownContent,
} from '@backstage/core-components';

const useStyles = makeStyles((theme) => ({
  versionSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 180,
  },
  noteBlock: {
    marginBottom: theme.spacing(2),
  },
  versionChip: {
    marginRight: theme.spacing(1),
  },
  noteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  dateText: {
    color: theme.palette.text.secondary,
  },
}));

interface ReleaseNote {
  id: string;
  service: string;
  version: string;
  releaseNotes: string;
  createdAt: string;
  updatedAt: string;
}

export const EntityReleaseNotesContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const serviceName =
    entity.metadata.annotations?.['shrek.devops.salestrak.io/service-name'] ??
    entity.metadata.name;

  const [latest, setLatest] = useState<ReleaseNote | null>(null);
  const [versions, setVersions] = useState<string[]>([]);
  const [fromVersion, setFromVersion] = useState('');
  const [toVersion, setToVersion] = useState('');
  const [rangeNotes, setRangeNotes] = useState<ReleaseNote[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const proxyBase = `${backendUrl}/api/proxy/shrek/service-release-notes`;

  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      setError(null);
      try {
        const [latestRes, versionsRes] = await Promise.all([
          fetch(`${proxyBase}/${serviceName}/latest`),
          fetch(`${proxyBase}/${serviceName}/versions`),
        ]);

        if (latestRes.ok) {
          const latestData = await latestRes.json();
          setLatest(latestData);
        }

        if (versionsRes.ok) {
          const versionsData = await versionsRes.json();
          setVersions(versionsData);
        }
      } catch (e: any) {
        setError(e.message ?? 'Failed to fetch release notes');
      } finally {
        setLoading(false);
      }
    };

    fetchInitial();
  }, [proxyBase, serviceName]);

  const fetchRange = useCallback(async () => {
    if (!fromVersion || !toVersion) return;
    setRangeLoading(true);
    try {
      const res = await fetch(
        `${proxyBase}/${serviceName}/range?from=${encodeURIComponent(fromVersion)}&to=${encodeURIComponent(toVersion)}`,
      );
      if (res.ok) {
        const data = await res.json();
        setRangeNotes(data);
      }
    } catch (e: any) {
      setError(e.message ?? 'Failed to fetch range');
    } finally {
      setRangeLoading(false);
    }
  }, [proxyBase, serviceName, fromVersion, toVersion]);

  const clearRange = () => {
    setFromVersion('');
    setToVersion('');
    setRangeNotes(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Error loading release notes"
        missing="info"
        description={error}
      />
    );
  }

  if (!latest && versions.length === 0) {
    return (
      <EmptyState
        title="No release notes available"
        missing="info"
        description={`No release notes have been published for "${serviceName}" yet.`}
      />
    );
  }

  const notesToShow = rangeNotes ?? (latest ? [latest] : []);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <InfoCard
          title="Version Range"
          subheader="Select a start and end version to view accumulated release notes"
        >
          <Box className={classes.versionSelector}>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.formControl}
            >
              <InputLabel>From version</InputLabel>
              <Select
                value={fromVersion}
                onChange={(e) => setFromVersion(e.target.value as string)}
                label="From version"
              >
                {versions.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              className={classes.formControl}
            >
              <InputLabel>To version</InputLabel>
              <Select
                value={toVersion}
                onChange={(e) => setToVersion(e.target.value as string)}
                label="To version"
              >
                {versions.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!fromVersion || !toVersion || rangeLoading}
              onClick={fetchRange}
            >
              {rangeLoading ? <CircularProgress size={20} /> : 'View Range'}
            </Button>

            {rangeNotes && (
              <Button variant="outlined" size="small" onClick={clearRange}>
                Show Latest
              </Button>
            )}
          </Box>
        </InfoCard>
      </Grid>

      <Grid item xs={12}>
        <InfoCard
          title={
            rangeNotes
              ? `Release Notes: ${fromVersion} → ${toVersion}`
              : 'Latest Release Notes'
          }
        >
          {notesToShow.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No release notes found for the selected range.
            </Typography>
          ) : (
            notesToShow.map((note, index) => (
              <Box key={note.id} className={classes.noteBlock}>
                {index > 0 && <Divider style={{ marginBottom: 16 }} />}
                <Box className={classes.noteHeader}>
                  <Chip
                    label={note.version}
                    color="primary"
                    size="small"
                    className={classes.versionChip}
                  />
                  <Typography variant="caption" className={classes.dateText}>
                    {new Date(note.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
                <MarkdownContent content={note.releaseNotes} />
              </Box>
            ))
          )}
        </InfoCard>
      </Grid>
    </Grid>
  );
};
